import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewChecked,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { backendRestApis } from '@environments/backend-rest-apis';
import { environment } from '@environments/environment';
import { marked } from 'marked';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

@Component({
  selector: 'app-ai-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.css',
})
export class AiChatComponent implements AfterViewChecked {
  private readonly http = inject(HttpClient);
  private baseUrl = `https://dev-api.mapsy.com.hr${backendRestApis.aiChat.ask}`; // TODO: PROMJENIIII, NEZ ZASTO NIJE RADILO, IMAM PREZENTACIJU DIPLOMSKOG
  private readonly promptMaxHeightPx = 126;

  @ViewChild('messagesEnd') private messagesEnd!: ElementRef<HTMLDivElement>;
  @ViewChild('promptInput') private promptInput!: ElementRef<HTMLTextAreaElement>;

  readonly messages = signal<ChatMessage[]>([]);
  readonly inputValue = signal('');
  readonly isLoading = signal(false);

  readonly suggestedPrompts = [
    'Koja mi je najduža vožnja?',
    'Koliko km sam ukupno prešao?',
    'Koliko vožnji sam imao ovaj mjesec?',
  ];

  private shouldScroll = false;
  private sanitizer = inject(DomSanitizer);

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  renderMarkdown(content: string): SafeHtml {
  const html = marked.parse(content, { async: false }) as string;
  return this.sanitizer.bypassSecurityTrustHtml(html);
}

  resizePromptInput(): void {
    const textarea = this.promptInput?.nativeElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, this.promptMaxHeightPx);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > this.promptMaxHeightPx ? 'auto' : 'hidden';
  }

  sendMessage(promptOverride?: string): void {
    const question = (promptOverride ?? this.inputValue()).trim();
    if (!question || this.isLoading()) {
      return;
    }

    this.messages.update((msgs) => [
      ...msgs,
      { role: 'user', content: question, timestamp: new Date() },
    ]);
    this.inputValue.set('');
    this.resizePromptInput();
    this.isLoading.set(true);
    this.shouldScroll = true;

    this.http
      .post(this.baseUrl, question, {
        headers: { 'Content-Type': 'text/plain' },
        responseType: 'text',
      })
      .subscribe({
        next: (response) => {
          this.messages.update((msgs) => [
            ...msgs,
            { role: 'assistant', content: response, timestamp: new Date() },
          ]);
          this.isLoading.set(false);
          this.shouldScroll = true;
        },
        error: () => {
          this.messages.update((msgs) => [
            ...msgs,
            {
              role: 'assistant',
              content:
                'Nisam uspio dohvatiti odgovor. Pokušaj ponovno za trenutak.',
              timestamp: new Date(),
            },
          ]);
          this.isLoading.set(false);
          this.shouldScroll = true;
        },
      });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private scrollToBottom(): void {
    try {
      this.messagesEnd.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    } catch {
      /* viewport not ready yet, ignore */
    }
  }
}